import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Determines whether the school/branch filter selectors on a page should be
 * frozen (locked to the user's assigned scope). Employees, branch admins, and
 * special-access users without multi-branch assignment cannot pick a different
 * branch — they must operate strictly within their assigned branch.
 *
 * Owners ("school_owner", "sub_owner") are never frozen so they can still
 * register more branches or switch context freely.
 *
 * Returns:
 *  - frozenBranch:    true => the branch selector must be disabled and locked
 *                     to lockedBranchId
 *  - frozenSchool:    true => the school selector must be disabled (single
 *                     school visibility)
 *  - lockedBranchId:  the only branch id the user can use (or null)
 *  - lockedSchoolId:  the only school id the user can use (or null)
 *  - isOwner:         convenience flag
 */
export function useBranchFreeze() {
  const { user } = useAuth();
  const { branchesForSelectedSchool, approvedSchools } = useBranch();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const frozenBranch = !isOwner && branchesForSelectedSchool.length <= 1;
  const frozenSchool = !isOwner && approvedSchools.length <= 1;
  return {
    isOwner,
    frozenBranch,
    frozenSchool,
    lockedBranchId: frozenBranch ? (branchesForSelectedSchool[0]?.id || null) : null,
    lockedSchoolId: frozenSchool ? (approvedSchools[0]?.id || null) : null,
  };
}
